// src/pages/analytics/payables/ApBillsTab.tsx
//
// Vendor bills (recipe ap-bills, agent 3.24.0+). In Advantage the purchase
// order is the bill (PurHead — what the QuickBooks sync pushes, DocNumber =
// PO); payments are made in QuickBooks, so open / paid lives on the A/P Aging
// tab. Filter by received / not received / not sent to QuickBooks, search by
// PO, vendor invoice # or vendor. Click a PO for its lines, ordered vs received.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, RefreshCw, Search } from 'lucide-react';
import { apiV2 as api } from '../../../services/apiV2';
import DateField from '../../../components/DateField';
import Modal from '../../../components/Modal';
import type { ApBill, ApBillStatus } from '../../../types/payables';
import { exportCsv, type CsvColumns } from '../../../lib/csvExport';
import { exportXlsx } from '../../../lib/xlsxExport';
import {
  ExportButtons, LoadError, Spinner, Tile, controlsCls, fmtCost, fmtCur, fmtCur2, fmtNum,
  isoDaysAgo, labelCls, pill, tableWrap, tdCls, tdR, thCls, thR,
} from './apShared';

const STATUSES: { value: ApBillStatus; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'received', label: 'Received' },
  { value: 'notReceived', label: 'Not received' },
  { value: 'notSent', label: 'Not sent to QB' },
];

const yesNo = (v: boolean | null) => (v == null ? '' : v ? 'Yes' : 'No');

const BILL_COLS: CsvColumns<ApBill> = [
  ['PO', r => r.po], ['Vendor invoice #', r => r.ref], ['Code', r => r.code], ['Vendor', r => r.name],
  ['Date', r => r.date], ['Due', r => r.due], ['Terms', r => r.terms], ['Required', r => r.reqDate ?? ''],
  ['Received', r => yesNo(r.received)], ['Received date', r => r.receivedDate ?? ''],
  ['Total', r => r.total], ['Sent to QuickBooks', r => yesNo(r.sentToQb)],
];

function SentBadge({ sent }: { sent: boolean | null }) {
  if (sent == null) return <span className="text-slate-600">–</span>;
  return sent ? <span className="text-emerald-400">Sent</span> : <span className="text-amber-300">Not sent</span>;
}

export function ApBillsTab() {
  const [dateFrom, setDateFrom] = useState(() => isoDaysAgo(90));
  const [dateTo, setDateTo] = useState(() => isoDaysAgo(0));
  const [status, setStatus] = useState<ApBillStatus>('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<ApBill | null>(null);

  const q = useQuery({
    queryKey: ['apBills', dateFrom, dateTo, status],
    queryFn: () => api.getApBills({ dateFrom, dateTo, status }),
    staleTime: 60_000,
    retry: 0,
  });

  const bills = useMemo(() => {
    const s = search.trim().toLowerCase();
    const rows = q.data?.bills ?? [];
    if (!s) return rows;
    return rows.filter(b => [b.po, b.ref, b.name, String(b.code)].some(x => x.toLowerCase().includes(s)));
  }, [q.data, search]);

  const totals = useMemo(() => ({
    total: bills.reduce((a, b) => a + b.total, 0),
    received: bills.filter(b => b.received).reduce((a, b) => a + b.total, 0),
    notReceived: bills.filter(b => !b.received).length,
    notSent: bills.filter(b => b.sentToQb === false).length,
  }), [bills]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2"><FileText size={20} /> Vendor Bills</h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Purchase orders in Advantage (each PO is the bill sent to QuickBooks). Payments are made in QuickBooks; see A/P Aging for what's open. Click a PO for its lines.
        </p>
      </div>

      <div className={controlsCls}>
        <DateField label="From" value={dateFrom} onChange={setDateFrom} />
        <DateField label="To" value={dateTo} onChange={setDateTo} />
        <div className="flex flex-col gap-1">
          <span className={labelCls}>Status</span>
          <div className="flex gap-1">
            {STATUSES.map(s => <button key={s.value} className={pill(status === s.value)} onClick={() => setStatus(s.value)}>{s.label}</button>)}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className={labelCls}>Search</span>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="PO, vendor inv #, vendor…"
              className="pl-6 pr-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white w-60 focus:outline-none focus:ring-1 focus:ring-blue-500/50" />
          </div>
        </div>
        <div className="flex-1" />
        <ExportButtons what="bills" disabled={!bills.length}
          onCsv={() => exportCsv('vendor-bills', BILL_COLS, bills)} onXlsx={() => exportXlsx('vendor-bills', BILL_COLS, bills)} />
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && <LoadError error={q.error} what="vendor bills" />}

      {q.isLoading ? <Spinner /> : !q.error && q.data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile tone="violet" label="Billed" value={fmtCur(totals.total)} sub={`${bills.length} POs${q.data.truncated ? ' (first 2,000)' : ''}`} />
            <Tile label="Received" value={fmtCur(totals.received)} />
            <Tile tone={totals.notReceived ? 'amber' : 'slate'} label="Not received" value={totals.notReceived} sub="POs" />
            <Tile tone={totals.notSent ? 'amber' : 'slate'} label="Not sent to QuickBooks" value={totals.notSent}
              sub="Advantage's sent-to-QB flag" />
          </div>

          <div className={`${tableWrap} max-h-[600px]`}>
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-900">
                <tr>
                  <th className={thCls}>PO</th><th className={thCls}>Vendor inv #</th><th className={thCls}>Vendor</th>
                  <th className={thCls}>Date</th><th className={thCls}>Due</th><th className={thCls}>Required</th>
                  <th className={thCls}>Received</th><th className={thR}>Total</th><th className={thCls}>QB</th>
                </tr>
              </thead>
              <tbody>
                {bills.map(b => (
                  <tr key={b.po} onClick={() => setOpen(b)} className="border-t border-slate-800/60 cursor-pointer hover:bg-slate-800/40">
                    <td className={tdCls}>{b.po}{b.isVoid && <span className="ml-1 text-red-400">void</span>}</td>
                    <td className={`${tdCls} text-slate-400`}>{b.ref}</td>
                    <td className={tdCls}>{b.name}</td>
                    <td className={tdCls}>{b.date}</td>
                    <td className={tdCls}>{b.due}</td>
                    <td className={`${tdCls} text-slate-400`}>{b.reqDate ?? '–'}</td>
                    <td className={tdCls}>{b.received ? <span className="text-emerald-400">{b.receivedDate ?? 'Yes'}</span> : <span className="text-amber-300">Not yet</span>}</td>
                    <td className={tdR}>{fmtCur2(b.total)}</td>
                    <td className={tdCls}><SentBadge sent={b.sentToQb} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {open && <BillModal bill={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function BillModal({ bill, onClose }: { bill: ApBill; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['apBill', bill.po],
    queryFn: () => api.getApBill(bill.po),
    staleTime: 60_000,
  });
  const lines = q.data?.lines ?? [];
  return (
    <Modal title={`PO ${bill.po} — ${bill.name}`} onClose={onClose} panelClassName="max-w-4xl">
      <div className="space-y-3 text-xs">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-slate-300">
          <div><span className="text-slate-500">Vendor inv #</span><div>{bill.ref || '–'}</div></div>
          <div><span className="text-slate-500">Date / due</span><div>{bill.date} → {bill.due}{bill.terms ? ` (${bill.terms})` : ''}</div></div>
          <div><span className="text-slate-500">Required / received</span><div>{bill.reqDate ?? '–'} / {bill.received ? bill.receivedDate ?? 'yes' : 'not yet'}</div></div>
          <div><span className="text-slate-500">Total / QuickBooks</span><div>{fmtCur2(bill.total)} / <SentBadge sent={bill.sentToQb} /></div></div>
        </div>
        {q.isLoading ? <Spinner /> : q.error ? <LoadError error={q.error} what="the PO lines" /> : (
          <div className={`${tableWrap} max-h-[420px]`}>
            <table className="w-full">
              <thead className="sticky top-0 bg-slate-900">
                <tr>
                  <th className={thCls}>#</th><th className={thCls}>Item</th><th className={thCls}>Description</th>
                  <th className={thR}>Ordered</th><th className={thR}>Received</th><th className={thR}>Short</th>
                  <th className={thR}>Cost</th><th className={thR}>Ext</th>
                </tr>
              </thead>
              <tbody>
                {lines.map(l => (
                  <tr key={l.line} className="border-t border-slate-800/60">
                    <td className={tdCls}>{l.line}</td>
                    <td className={tdCls}>{l.item}</td>
                    <td className={`${tdCls} text-slate-400`}>{l.desc}</td>
                    <td className={tdR}>{fmtNum(l.qty)}</td>
                    <td className={tdR}>{l.received != null ? fmtNum(l.received) : '–'}</td>
                    <td className={`${tdR} ${l.shortQty > 0 ? 'text-amber-300' : 'text-slate-600'}`}>{l.shortQty > 0 ? fmtNum(l.shortQty) : '–'}</td>
                    <td className={tdR}>{fmtCost(l.cost)}</td>
                    <td className={tdR}>{fmtCur2(l.ext)}</td>
                  </tr>
                ))}
                {q.data && lines.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-4 text-center text-slate-500">No item lines on this PO.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
